import { DatatableHead } from '@monksflow/monks-ui';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';

export type SortDirection = 'asc' | 'desc';

interface SortIconProps<TColumn extends string> {
  column: TColumn;
  sortBy: TColumn;
  sortDirection: SortDirection;
}

/**
 * Neutral double-arrow for an inactive column, directional arrow for the active one — so a glance
 * at the header row tells you both which column is sorting and which way.
 */
function SortIcon<TColumn extends string>({
  column,
  sortBy,
  sortDirection,
}: SortIconProps<TColumn>) {
  // Tagged for tests: the icon renders as an `aria-hidden` svg with only a lucide class to identify
  // it, which no Testing Library query can reach. Same approach as `UploadItemRow`.
  const testId = `sort-icon-${column}`;
  if (sortBy !== column)
    return <ArrowUpDown className="size-3.5 text-muted-foreground" data-testid={testId} />;
  return sortDirection === 'asc' ? (
    <ArrowUp className="size-3.5" data-testid={testId} />
  ) : (
    <ArrowDown className="size-3.5" data-testid={testId} />
  );
}

export interface SortableHeadProps<TColumn extends string> {
  column: TColumn;
  label: string;
  className?: string;
  sortBy: TColumn;
  sortDirection: SortDirection;
  /** Fired with this column; the caller decides how the direction toggles. */
  onSort: (column: TColumn) => void;
}

/**
 * Clickable `DatatableHead` carrying a sort indicator.
 *
 * Keep it module-scope in consumers (don't define a wrapper inside a component body): the header
 * cells would otherwise be torn down and rebuilt on every parent render — which, on a table with a
 * search box, is every keystroke before the debounce fires.
 */
export function SortableHead<TColumn extends string>({
  column,
  label,
  className,
  sortBy,
  sortDirection,
  onSort,
}: SortableHeadProps<TColumn>) {
  const ariaSort =
    // eslint-disable-next-line i18next/no-literal-string -- aria attribute values, not copy
    sortBy === column ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none';

  return (
    <DatatableHead
      aria-sort={ariaSort}
      className={`cursor-pointer select-none ${className ?? ''}`}
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        <SortIcon column={column} sortBy={sortBy} sortDirection={sortDirection} />
      </div>
    </DatatableHead>
  );
}
